import {
  ChecklistStatus,
  ChecklistTemplateKind,
  useChecklist,
  useChecklistUpdate
} from '@domain/hroc-checklists'
import {DataContentWrapper} from '@hconnect/common/containers'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {
  ChecklistForm,
  HrocChecklistsPathSegments,
  useChecklistConfig,
  type StepFormRef,
  type SubmitButtonState
} from '@widgets/hroc-checklists'
import {AxiosError} from 'axios'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {FEATURE_NAMESPACE} from '../consts'

type Props = {
  checklistId: string
  plantId: string
  backToTargetLabel: string
  onCancel: () => void
  stepFormRef?: React.RefObject<StepFormRef>
  onSubmitButtonStateChange: (state: SubmitButtonState) => void
}

export const ChecklistContainer = ({
  checklistId,
  plantId,
  backToTargetLabel,
  onCancel,
  stepFormRef,
  onSubmitButtonStateChange
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()

  const {
    data: checklist,
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useChecklist({id: checklistId, plantId})

  const handleUpdateSuccess = () => {
    spawnSuccessToast(t('checklist.update.success'))
  }
  const handleUpdateError = (error: AxiosError) => {
    const errorMessage =
      error.response?.status === 409
        ? t('checklist.update.errorConflict')
        : t('checklist.update.error')
    spawnErrorToast(errorMessage)
  }

  const updateMutation = useChecklistUpdate(
    {id: checklistId, plantId},
    {
      onSuccess: handleUpdateSuccess,
      onError: handleUpdateError
    }
  )

  const isReadOnly = checklist?.status === ChecklistStatus.DONE
  const isKilnCheckSheetEnabled = useCommonFeatureFlag(FeatureFlagKey.ChecklistsKilnCheckSheet, {
    plantId
  })
  const checklistConfig = useChecklistConfig(checklist?.kind)

  const hasRequiredData = checklist && checklistConfig
  const hasMissingConfig = checklist && !checklistConfig

  const isUnsupportedChecklistKind =
    checklist &&
    !isLoading &&
    checklist.kind === ChecklistTemplateKind.KilnCheckSheet &&
    isKilnCheckSheetEnabled === false

  useEffect(() => {
    if (isUnsupportedChecklistKind) {
      void navigate(HrocChecklistsPathSegments.UnsupportedKind, {replace: true})
    }
  }, [isUnsupportedChecklistKind, navigate])

  if (isUnsupportedChecklistKind) {
    return null
  }

  return (
    <DataContentWrapper
      showLoader={isLoading}
      isError={isError}
      isRefetching={isRefetching}
      isNoData={!hasRequiredData}
      retryFunction={() => void refetch()}
      noDataSettings={
        hasMissingConfig
          ? {noDataMessage: t('checklist.error.unsupportedChecklistType')}
          : undefined
      }
    >
      {hasRequiredData && (
        <ChecklistForm
          checklist={checklist}
          checklistConfig={checklistConfig}
          plantId={plantId}
          readOnly={isReadOnly}
          updateMutation={updateMutation}
          onCancel={onCancel}
          backToTargetLabel={backToTargetLabel}
          stepFormRef={stepFormRef}
          onSubmitButtonStateChange={onSubmitButtonStateChange}
        />
      )}
    </DataContentWrapper>
  )
}
